import * as CL from '@design-system/component-library';
import { type JSX, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useNavigate } from 'react-router';
import { pushToDataLayer } from '../../common/analytics.js';
import { dsClass } from '../../common/constants/dsClasses.js';
import { paths } from '../../common/constants/pathVariables.js';
import { DialogType, StatusColor } from '../../common/enums.js';
import { useSearchParams } from '../../common/hooks/useSearchParams.js';
import {
  Locale,
  askAboutInvoiceMsg,
  billingAccountExtensionNameMsg,
  billingAccountMsg,
  billingAccountNameMsg,
  changeBillingAddressMsg,
  changeDeliveryMethodMsg,
  deliveryMethodMsg,
  dueDateMsg,
  eInvoicingAddressMsg,
  eInvoicingOperatorMsg,
  invoiceInstructionsPart1Item1Msg,
  invoiceInstructionsPart1Item2Msg,
  invoiceInstructionsPart1Item3Msg,
  invoiceInstructionsPart1LinkMsg,
  invoiceInstructionsPart2Msg,
  invoiceInstructionsPart2NewInvoiceMsg,
  invoiceInstructionsPart3LinkMsg,
  invoiceInstructionsPart3Msg,
  invoiceInstructionsSubtitle1Msg,
  invoiceInstructionsSubtitle2Msg,
  invoiceInstructionsSubtitle3Msg,
  invoiceInstructionsTitleMsg,
  invoiceLanguageMsg,
  invoiceMsg,
  invoicesMsg,
  movePaymentDateMsg,
  omaElisaForCompaniesMsg,
  openMsg,
  paidMsg,
  payMsg,
  payerDetailsMsg,
  payerMsg,
  recipientEmailMsg,
  recipientMsg,
  referenceMsg,
  t,
} from '../../common/i18n/index.js';
import { getCompanyName } from '../../common/utils/accountUtils.js';
import {
  getDeliveryMethod,
  getElectronicInvoiceOperatorDisplayValue,
  isBillingAccountCancelled,
  isBillingAccountInSfdc,
} from '../../common/utils/billingAccountUtils.js';
import {
  formatTimestampToDDMMYYYY,
  formatTimestampToUTCDDMMYYYY,
  formatTimestampToUTCYYYYMMDD,
  formatTimestampToYYYYMMDD,
} from '../../common/utils/dateUtils.js';
import { getLanguageDisplayValue } from '../../common/utils/languageUtils.js';
import { deepEqual } from '../../common/utils/objectUtils.js';
import { formatBalance, formatSum } from '../../common/utils/priceUtils.js';
import { BillingAccountDeliveryMethod, ContactType, Invoice } from '../../generated/api/models.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { useConfig } from '../../public/site/ConfigProvider.js';
import { SiteContext } from '../../public/site/SiteContext.js';
import { openDialog } from '../../public/site/dialogStore.js';
import { finalizePayment, finalizePaymentFailed } from '../../selfservice/actions/index.js';
import { setInvoicePaymentMdmId } from '../../selfservice/common/localStorageUtils';
import { DetailsHeading } from '../DetailsHeading/DetailsHeading.js';
import { DetailsWithCard } from '../DetailsWithInfoColumn/DetailsWithInfoColumn.js';
import { GenericInfoDialog } from '../Dialogs/GenericInfoDialog.js';
import { LinkableAccordion } from '../LinkableAccordion/index.js';
import { Loading } from '../Loading/index.js';
import { OpenCasesNotification } from '../OpenCasesNotification/OpenCasesNotification.js';
import { Status } from '../Status/Status.js';
import type { CLDescriptionItem } from '../../common/types/componentLibrary.js';
import type { DialogParams } from '../../common/types/dialog.js';
import type { BillChannel, BillingAccount, SupportCaseHeader } from '../../generated/api/models.js';
import type { ConfigState } from '../../public/site/configStore.js';
import type { State } from '../../selfservice/common/store.js';
import type { BreadCrumbList } from '../BreadCrumbs/BreadCrumbs.js';
import type { ButtonProps } from '@design-system/component-library';

import './InvoiceDetails.scss';

export const getStatusColumnInvoiceState = (balance = 0, due: number): { text: string; color: StatusColor } => {
  if (balance <= 0) {
    return {
      color: StatusColor.GREEN,
      text: t.RJ27(paidMsg),
    };
  }
  if (formatTimestampToYYYYMMDD(Date.now())! > formatTimestampToUTCYYYYMMDD(due)!) {
    return {
      color: StatusColor.RED,
      text: t.YRYX('Overdue'),
    };
  }
  return {
    color: StatusColor.GREY,
    text: t.TJ54(openMsg),
  };
};

export interface InvoiceDetailsProps {
  billChannels?: BillChannel[];
  billingAccount?: BillingAccount;
  invoice?: Invoice;
  openSupportCases?: SupportCaseHeader[];
  mdmId: string;
}

const MILLISECONDS_IN_A_DAY = 24 * 60 * 60 * 1000;
export const isTimeWithinLast24Hrs = (time: number) => {
  return new Date().getTime() - time < MILLISECONDS_IN_A_DAY;
};

function pdfLinkify(invoiceId: string): string {
  return `/api/ui/v2/private/invoices/${invoiceId}/pdf`;
}

const BillingAccountLinkElement = ({
  billingAccount,
  config,
  linkId,
  linkText,
  color,
  mdmId,
}: {
  billingAccount: BillingAccount;
  config: ConfigState;
  linkId: string;
  linkText: string;
  color?: ButtonProps['color'];
  mdmId: string;
}): JSX.Element => {
  const navigate = useNavigate();
  if (isBillingAccountCancelled(billingAccount)) {
    return <>{linkText}</>;
  }
  return isBillingAccountInSfdc(billingAccount) ? (
    <CL.Button
      id={linkId}
      color={color ?? 'link'}
      onClick={() => {
        if (billingAccount.billingAccountDisplayId) {
          const path = `${generatePath(paths.BILLING_ACCOUNT, {
            billingAccountId: billingAccount.billingAccountDisplayId,
          })}?mdmId=${mdmId}`;
          navigate(path);
        }
      }}
    >
      {linkText}
    </CL.Button>
  ) : (
    <a id={linkId} href={`${config.classicSiteUrl}/laskutustiedot`}>
      {linkText}
    </a>
  );
};

const getBillingAccountDescriptionItems = (
  billingAccount: BillingAccount,
  config: ConfigState,
  mdmId: string,
  billChannels?: BillChannel[]
): CLDescriptionItem[] => {
  const billingAccountDescriptionItems: CLDescriptionItem[] = [
    {
      title: t.PB6S(payerMsg),
      description: billingAccount.payerName,
    },
    {
      title: t.IFT9(billingAccountMsg),
      description: (
        <BillingAccountLinkElement
          billingAccount={billingAccount}
          config={config}
          linkId="to-billing-account-details-top"
          linkText={billingAccount.billingAccountDisplayId || ''}
          mdmId={mdmId}
        />
      ),
    },
    { title: t.RH6T(billingAccountNameMsg), description: billingAccount.billingAccountName },
    { title: t.KUTS(billingAccountExtensionNameMsg), description: billingAccount.billingAccountExtensionName },
    { title: t.YLAI(payerDetailsMsg), description: billingAccount.payerNameExtension },
    {
      title: t.LVRN(referenceMsg) + ' 1',
      description: billingAccount.customerReference1,
    },
    {
      title: t.LVRN(referenceMsg) + ' 2',
      description: billingAccount.customerReference2,
    },
    {
      title: t.LVRN(referenceMsg) + ' 3',
      description: billingAccount.customerReference3,
    },
    {
      title: t.LVRN(referenceMsg) + ' 4',
      description: billingAccount.customerReference4,
    },
    {
      title: t.G0QN(deliveryMethodMsg),
      description: getDeliveryMethod(billingAccount.deliveryMethod),
    },
  ];

  if (
    billingAccount.deliveryMethod === BillingAccountDeliveryMethod.ELECTRONIC &&
    billingAccount.billElectronicOperator
  ) {
    billingAccountDescriptionItems.push(
      {
        title: t.WVLB(eInvoicingOperatorMsg),
        description: getElectronicInvoiceOperatorDisplayValue(billingAccount.billElectronicOperator, billChannels),
      },
      {
        title: t.OL7B(eInvoicingAddressMsg),
        description: billingAccount.billElectronicAddress ?? '—',
      }
    );
  } else if (billingAccount.deliveryMethod === BillingAccountDeliveryMethod.EMAIL) {
    billingAccountDescriptionItems.push({
      title: billingAccount.billReceiverType === ContactType.PERSON ? t.SY1D(recipientMsg) : t.W1PP(recipientEmailMsg),

      description:
        billingAccount.billReceiverType === ContactType.PERSON
          ? billingAccount.billReceiverName
          : billingAccount.billReceiverEmail,
    });
  }

  billingAccountDescriptionItems.push({
    title: t.A7DR(invoiceLanguageMsg),
    description: getLanguageDisplayValue(billingAccount.billLanguage),
  });

  return billingAccountDescriptionItems.filter(item => item.description);
};

const InvoiceInstructionsCard = () => {
  const { siteLanguage } = useContext(SiteContext);
  return (
    <>
      <h3>{t.YAMO(invoiceInstructionsTitleMsg)}</h3>
      <h4 className={`${dsClass.MARGIN_TOP_4} ${dsClass.MARGIN_BOTTOM_0}`}>
        {t.UIV1(invoiceInstructionsSubtitle1Msg)}
      </h4>
      <ul className={dsClass.TEXT_S}>
        <li>{t.XXXQ(invoiceInstructionsPart1Item1Msg)}</li>
        <li>{t.VT5Z(invoiceInstructionsPart1Item2Msg)}</li>
        <li>{t.BQK6(invoiceInstructionsPart1Item3Msg)}</li>
      </ul>
      <CL.Link
        className={`${dsClass.TEXT_S} ${dsClass.MARGIN_TOP_4}`}
        linkTarget="_blank"
        linkHref={generatePath(
          siteLanguage === Locale.FI ? paths.INSTRUCTION_INVOICE_SUM_DIFFERS : paths.INSTRUCTION_INVOICE_SUM_DIFFERS_EN
        )}
        rel="noreferrer"
      >
        {t.GR8T(invoiceInstructionsPart1LinkMsg)}{' '}
        <CL.Icon icon="external-link" size="s" type="regular" color="blue-600" />
      </CL.Link>
      <h4 className={`${dsClass.MARGIN_TOP_4} ${dsClass.MARGIN_BOTTOM_0}`}>
        {t.V6WM(invoiceInstructionsSubtitle2Msg)}
      </h4>
      <p className={`${dsClass.TEXT_S} ${dsClass.MARGIN_TOP_0}`}>{t.WGXF(invoiceInstructionsPart2Msg)}</p>
      <h4 className={`${dsClass.MARGIN_TOP_4} ${dsClass.MARGIN_BOTTOM_0}`}>
        {t.MB13(invoiceInstructionsSubtitle3Msg)}
      </h4>
      <p className={`${dsClass.TEXT_S} ${dsClass.MARGIN_TOP_0} ${dsClass.MARGIN_BOTTOM_0}`}>
        {t.QP30(invoiceInstructionsPart3Msg)}
      </p>
      <CL.Link
        className={`${dsClass.TEXT_S} ${dsClass.MARGIN_TOP_4}`}
        linkTarget="_blank"
        linkHref={generatePath(
          siteLanguage === Locale.FI ? paths.INSTRUCTIONS_INVOICES : paths.INSTRUCTIONS_INVOICES_EN
        )}
        rel="noreferrer"
      >
        {t.BE0G(invoiceInstructionsPart3LinkMsg)}{' '}
        <CL.Icon icon="external-link" size="s" type="regular" color="blue-600" />
      </CL.Link>
    </>
  );
};

const InternalButtons = ({ mdmId, billingAccount }: { mdmId: string; billingAccount?: BillingAccount }) => {
  const navigate = useNavigate();
  return (
    <>
      <CL.Button
        color="link"
        onClick={() => {
          if (billingAccount?.billingAccountDisplayId) {
            const navigateToPath = `${generatePath(paths.BILLING_ACCOUNT, {
              billingAccountId: billingAccount.billingAccountDisplayId,
            })}?mdmId=${mdmId}`;
            navigate(navigateToPath, {
              state: {
                redirectToPath: `${location.pathname}?mdmId=${mdmId}`,
                redirectMessage: t.OLLP('Moving back to invoice details'),
              },
            });
          }
        }}
      >
        <>
          <span className="ea-icon ea-icon--small ea-icon--invoice" />
          {t.LZMG(changeDeliveryMethodMsg)}
        </>
      </CL.Button>
      <CL.Button
        color="link"
        onClick={() => {
          if (billingAccount?.billingAccountDisplayId) {
            const navigateToPath = `${generatePath(paths.BILLING_ACCOUNT, {
              billingAccountId: billingAccount.billingAccountDisplayId,
            })}?mdmId=${mdmId}`;
            navigate(navigateToPath, {
              state: {
                redirectToPath: `${location.pathname}?mdmId=${mdmId}`,
                redirectMessage: t.OLLP('Moving back to invoice details'),
              },
            });
          }
        }}
      >
        <>
          <span className="ea-icon ea-icon--small ea-icon--mappin" />
          {t.HKFZ(changeBillingAddressMsg)}
        </>
      </CL.Button>
    </>
  );
};

const ExternalLinks = ({ config }: { config: ConfigState }) => {
  return (
    <>
      <a
        href={`${config.classicSiteUrl}/laskutustiedot`}
        className={`${dsClass.BUTTON} ${dsClass.BUTTON_COLOR_LINK} ${dsClass.BUTTON_SIZE_M}`}
      >
        <span className="ea-icon ea-icon--small ea-icon--invoice" />
        {t.LZMG(changeDeliveryMethodMsg)}
      </a>
      <a
        href={`${config.classicSiteUrl}/laskutustiedot`}
        className={`${dsClass.BUTTON} ${dsClass.BUTTON_COLOR_LINK} ${dsClass.BUTTON_SIZE_M}`}
      >
        <span className="ea-icon ea-icon--small ea-icon--mappin" />
        {t.HKFZ(changeBillingAddressMsg)}
      </a>
    </>
  );
};

const InvoicePaidSection = ({ size = 'l' }: { size?: 's' | 'm' | 'l' | undefined }): JSX.Element => {
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <>
      <CL.Button
        size={size}
        color="secondary"
        onClick={() => {
          pushToDataLayer({
            event: 'button_click',
            // eslint-disable-next-line @typescript-eslint/naming-convention
            click_element_parent: 'NOE invoice details - I have paid button',
          });
          setDialogOpen(true);
        }}
      >
        {t.RKUQ('I have paid the invoice')}
      </CL.Button>

      {dialogOpen && (
        <GenericInfoDialog
          header={t.M57F('Thank you!')}
          body={
            <>
              {t.CPRM(
                'Your payment will be updated to us within 3 business days. You do not need to notify us separately about the payment.'
              )}
            </>
          }
          onCloseDialog={() => setDialogOpen(false)}
        />
      )}
    </>
  );
};

export const InvoiceDetails = ({
  billChannels,
  billingAccount,
  invoice,
  openSupportCases,
  mdmId,
}: InvoiceDetailsProps) => {
  let content: JSX.Element;
  const dispatch = useDispatch();
  const onShowDialog = (params: DialogParams) => openDialog(params);
  const invoiceDisplayId = invoice ? invoice.invoiceDisplayId : '…';
  const { invoiceId = null } = invoice || {};
  // Refer to Nets api specification: https://developers.nets.eu/netaxept/en-EU/api/response-codes/
  const searchParams = useSearchParams<{
    maksa: string;
    responseCode?: string;
    transactionId: string;
  }>();
  const { transactionId, maksa: triggerPayment, responseCode } = searchParams;
  const config = useConfig();
  const payments = useSelector((state: State) => state.payments, deepEqual);
  const { authenticatedUser } = useAuth();
  const companyName = getCompanyName(authenticatedUser, mdmId);

  useEffect(() => {
    if (invoiceId && transactionId && responseCode) {
      if (responseCode === 'OK') {
        dispatch(finalizePayment(invoiceId, transactionId));
      } else {
        dispatch(finalizePaymentFailed(`External payment system returned '${responseCode}' code`, 400));
      }
    }
  }, [dispatch, invoiceId, responseCode, transactionId]);

  useEffect(() => {
    if (triggerPayment !== undefined && invoice) {
      onShowDialog({
        balance: invoice.balance || 0,
        invoiceId: invoice.invoiceId,
        type: DialogType.BANK_BUTTONS,
      });
    }
  }, [invoice, triggerPayment]); /* TODO: rules-of-hooks */

  if (payments && payments.paymentCheckInProgress === true) {
    content = <Loading />;
  } else if (invoice && invoiceId && billingAccount) {
    const state = getStatusColumnInvoiceState(invoice.balance || 0, invoice.due);
    const pdfLink = pdfLinkify(invoiceId);
    const showInvoiceBalance: boolean = invoice.balance !== invoice.sum && invoice.balance !== 0;

    const invoiceDetailsDescriptionItems: CLDescriptionItem[] = [
      {
        title: t.YE3V('Invoice status'),
        description: <Status color={state.color} textAfterBadge={state.text} />,
      },
      {
        title: t.BVO5('Billing period'),
        description: invoice.billPeriod,
      },
      {
        title: t.LA93(dueDateMsg),
        description: formatTimestampToUTCDDMMYYYY(invoice.due),
      },
      {
        title: t.RJ27(paidMsg),
        description: formatBalance(invoice.sum, invoice.balance || 0),
      },
      {
        title: t.TJ3Q('Total taxable sum'),
        description: formatSum(invoice.sum),
      },
    ];

    if (showInvoiceBalance) {
      invoiceDetailsDescriptionItems.push({
        title:
          invoice.balance === undefined || invoice.balance < 0 ? t.AX9O('Overpayment') : t.Q4V8('Outstanding fees'),
        description: formatSum(invoice.balance),
      });
    }
    if (
      invoice.balance !== undefined &&
      invoice.balance > 0 &&
      invoice.sourceSystem !== Invoice.SourceSystemEnum.SFDC
    ) {
      invoiceDetailsDescriptionItems.push({
        description: invoice.balance ? (
          <div className="of-invoice-details__description-pay-buttons">
            <CL.Link
              linkStyle="link-button"
              buttonColor="primary"
              buttonSize="l"
              linkHref={`${config.classicSiteUrl}/laskut/currentId/${invoiceDisplayId}`}
            >
              {t.LK73(payMsg)}
            </CL.Link>
            <InvoicePaidSection size="m" />
          </div>
        ) : (
          ''
        ),
      });
    } else if (
      invoice.balance !== undefined &&
      invoice.balance > 0 &&
      invoice.sourceSystem === Invoice.SourceSystemEnum.SFDC
    ) {
      invoiceDetailsDescriptionItems.push({
        description: (
          <div className="of-invoice-details__description-pay-buttons">
            <CL.Button
              size="l"
              onClick={() => {
                setInvoicePaymentMdmId(mdmId);
                onShowDialog({
                  balance: invoice.balance || 0,
                  invoiceId: invoice.invoiceId,
                  type: DialogType.BANK_BUTTONS,
                });
              }}
            >
              {t.LK73(payMsg)}
            </CL.Button>
            <InvoicePaidSection />
          </div>
        ),
      });
      invoiceDetailsDescriptionItems.push({
        description: (
          <CL.Button
            color="light"
            onClick={() =>
              onShowDialog({
                invoiceId,
                type: DialogType.MOVE_PAYMENT_DATE,
              })
            }
          >
            {t.YW9I(movePaymentDateMsg)}
          </CL.Button>
        ),
      });
    }

    const hasOpenSupportCases = (openSupportCases || []).length > 0;

    const getBillingAccountElements = () => {
      if (isBillingAccountInSfdc(billingAccount)) {
        if (isBillingAccountCancelled(billingAccount)) {
          // case ba not active, no need for buttons
          return <></>;
        } else {
          return <InternalButtons mdmId={mdmId} billingAccount={billingAccount} />;
        }
      } else {
        return <ExternalLinks config={config} />;
      }
    };

    content = (
      <div className="of-invoice-details__content">
        <CL.Description
          items={invoiceDetailsDescriptionItems}
          className={`${dsClass.MARGIN_0} invoice-details-description`}
        />

        {hasOpenSupportCases && <OpenCasesNotification supportCases={openSupportCases || []} />}

        {invoice && isTimeWithinLast24Hrs(invoice.created) && (
          <CL.Disclaimer
            className={!hasOpenSupportCases ? dsClass.MARGIN_TOP_3 : ''}
            text={t.Y30X(invoiceInstructionsPart2NewInvoiceMsg)}
            visible
          />
        )}

        <div className="of-invoice-details__actions">
          <a
            className={`${dsClass.BUTTON} ${dsClass.BUTTON_COLOR_LINK} ${dsClass.BUTTON_SIZE_M}`}
            href={pdfLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="ea-icon ea-icon--small ea-icon--pdf-file" /> {t.WURW('Open invoice')}
          </a>
          <CL.Button
            color="link"
            onClick={() =>
              mdmId
                ? onShowDialog({
                    invoiceDisplayId,
                    invoiceId,
                    companyName: companyName,
                    accountMasterId: mdmId,
                    type: DialogType.ASK_ABOUT_INVOICE,
                  })
                : () => {
                    // throw Error here to allow returning from Nets without mdmId
                    throw new Error('Missing mdmId');
                  }
            }
          >
            <>
              <span className="ea-icon ea-icon--small ea-icon--omaguru" />
              {t.AM3R(askAboutInvoiceMsg)}
            </>
          </CL.Button>
          {getBillingAccountElements()}
        </div>
        <div className={dsClass.PADDING_TOP_5}>
          <LinkableAccordion
            heading={t.AMRD('Billing account details')}
            headingLevel="h3"
            id="billing-account-info"
            defaultOpen={false}
          >
            <CL.Description
              items={getBillingAccountDescriptionItems(billingAccount, config, mdmId, billChannels)}
              className={`${dsClass.MARGIN_0} ${dsClass.PADDING_BOTTOM_4}`}
            />
            <div className={dsClass.PADDING_BOTTOM_5}>
              <BillingAccountLinkElement
                billingAccount={billingAccount}
                config={config}
                color="light"
                linkId="to-billing-account-details-bottom"
                linkText={
                  isBillingAccountCancelled(billingAccount)
                    ? t.N7W5('The billing account has been terminated, so you cannot edit its details.')
                    : t.V19M('See billing account')
                }
                mdmId={mdmId}
              />
              <div className={dsClass.PADDING_TOP_5}>
                <small>
                  ({t.NALH('Billing agreement modified')} {formatTimestampToDDMMYYYY(billingAccount.lastModified)})
                </small>
                <br />
                <small>{t.YM3W('Showing the current information of the billing account.')}</small>
              </div>
            </div>
          </LinkableAccordion>
          <LinkableAccordion
            heading={t.EE4N('Payment details')}
            headingLevel="h3"
            id="payment-details"
            defaultOpen={false}
          >
            <CL.Description
              items={[
                {
                  title: t.FY9Z('Account number'),
                  description: (
                    <div className="of-invoice-details__accounts">
                      <small>Danske Bank A/S</small>
                      <span>FI87 8000 1770 2297 98</span>
                      <small>Nordea</small>
                      <span>FI48 1574 3000 0101 89</span>
                      <small>OP</small>
                      <span>FI03 5000 0120 1496 11</span>
                    </div>
                  ),
                },
                {
                  title: t.P87N('Reference number'),
                  description: invoice.referenceNumber,
                },
              ]}
              className={`${dsClass.MARGIN_0} ${dsClass.PADDING_BOTTOM_4}`}
            />
          </LinkableAccordion>
        </div>
      </div>
    );
  } else {
    content = <Loading />;
  }

  const breadcrumbPaths: BreadCrumbList = [
    { name: t.VCUZ(omaElisaForCompaniesMsg), path: paths.SELF_SERVICE_HOME },
    { name: t.Y7C0(invoicesMsg), path: paths.INVOICES },
    { name: invoiceDisplayId },
  ];

  return (
    <div className="of-invoice-details">
      <DetailsHeading
        breadcrumbPaths={breadcrumbPaths}
        heading={invoiceDisplayId}
        headingTop={t.JF65(invoiceMsg)}
        headingBottom={companyName}
        heroPicto="invoice"
      />
      <DetailsWithCard
        cardId="invoice-instructions-card"
        cardImage="1lPDFnev8TCQXJTn8ZmV0o/i.svg"
        cardContent={<InvoiceInstructionsCard />}
      >
        {content}
      </DetailsWithCard>
    </div>
  );
};
